import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TenantUsage } from '@order-pilot/shared';
import { useQuery } from '@tanstack/react-query';
import { AsyncSection } from '@/components/AsyncSection';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { EmptyState, Skeleton } from '@/components/States';
import { fetchPlatformUsage, usageKeys } from '@/features/plans/api';
import {
  DEFAULT_STALE_MINUTES,
  fetchPlatformSyncHealth,
  syncHealthKeys,
} from '@/features/sync-health/api';

function KpiTile({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-semibold ${
          tone === 'warn' ? 'text-amber-700' : 'text-slate-900'
        }`}
      >
        {value}
      </div>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

/** "712 / 600 bills per day" — the figure and the limit it crossed. */
function OverBy({
  actual,
  limit,
  unit,
}: {
  actual: number;
  limit: number | null;
  unit: string;
}): ReactNode {
  if (limit === null) return null;
  return (
    <li>
      <span className="font-medium text-amber-800">
        {actual.toLocaleString(undefined, { maximumFractionDigits: 1 })}
      </span>
      <span className="text-slate-500"> / {limit.toLocaleString()} {unit}</span>
    </li>
  );
}

function OverLimitRow({ usage }: { usage: TenantUsage }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-3 last:border-b-0">
      <div className="min-w-0">
        <Link
          to={`/cp/tenants/${usage.tenantId}`}
          className="font-medium text-brand-700 hover:underline"
        >
          {usage.tenantName}
        </Link>
        <p className="text-xs text-slate-500">{usage.plan?.name ?? 'No plan'}</p>
      </div>
      <ul className="space-y-0.5 text-right text-sm">
        {usage.overBills ? (
          <OverBy
            actual={usage.avgBillsPerDay}
            limit={usage.plan?.maxBillsPerDay ?? null}
            unit="bills per day"
          />
        ) : null}
        {usage.overBranches ? (
          <OverBy
            actual={usage.branchCount}
            limit={usage.plan?.maxBranches ?? null}
            unit="branches"
          />
        ) : null}
        {usage.overDevices ? (
          <OverBy
            actual={usage.deviceCount}
            limit={usage.plan?.maxDevices ?? null}
            unit="tills"
          />
        ) : null}
      </ul>
    </div>
  );
}

/**
 * The SA landing screen: what needs attention, before the tenant list.
 *
 * Plan limits warn here and nowhere else — nothing in the system blocks a
 * tenant for exceeding one, so this page opening on the over-limit list is
 * the entire enforcement mechanism.
 */
export function AdminDashboardPage() {
  const usageQuery = useQuery({
    queryKey: usageKeys.platform(),
    queryFn: ({ signal }) => fetchPlatformUsage(signal),
  });

  // Reused from the sync-health screen rather than refetched differently —
  // one definition of "stale" across the console.
  const healthQuery = useQuery({
    queryKey: syncHealthKeys.platform(DEFAULT_STALE_MINUTES),
    queryFn: ({ signal }) =>
      fetchPlatformSyncHealth(DEFAULT_STALE_MINUTES, signal),
  });

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Tenants over their plan, and tills that have gone quiet."
      />

      <AsyncSection
        state={usageQuery}
        skeleton={
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        }
        errorTitle="Could not load plan usage"
      >
        {(usage) => {
          const overLimit = usage.tenants.filter((row) => row.isOverLimit);
          const unassigned = usage.tenants.filter(
            (row) => row.plan === null,
          ).length;

          return (
            <>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KpiTile label="Tenants" value={usage.tenants.length.toString()} />
                <KpiTile
                  label="Over plan limit"
                  value={usage.overLimitCount.toString()}
                  hint={`${usage.windowDays}-day average`}
                  tone={usage.overLimitCount > 0 ? 'warn' : 'default'}
                />
                <KpiTile
                  label="No plan assigned"
                  value={unassigned.toString()}
                  hint={unassigned > 0 ? 'Never flagged as over' : undefined}
                />
                <KpiTile
                  label="Stale tills"
                  value={
                    healthQuery.data?.staleDeviceCount?.toString() ?? '—'
                  }
                  hint="No contact in the last hour"
                  tone={
                    (healthQuery.data?.staleDeviceCount ?? 0) > 0
                      ? 'warn'
                      : 'default'
                  }
                />
              </div>

              <div className="mt-6">
                <SectionCard
                  headingId="over-limit"
                  title="Over their plan"
                  count={overLimit.length}
                  description={`Measured over the last ${usage.windowDays} days. Limits warn only — these tills keep trading.`}
                >
                  {overLimit.length === 0 ? (
                    <EmptyState
                      title="Every tenant is inside their plan"
                      description="Nothing to chase. A tenant that outgrows their tier will appear here."
                      compact
                    />
                  ) : (
                    <div>
                      {overLimit.map((row) => (
                        <OverLimitRow key={row.tenantId} usage={row} />
                      ))}
                    </div>
                  )}
                </SectionCard>
              </div>
            </>
          );
        }}
      </AsyncSection>
    </div>
  );
}
